"use client";
import type * as React from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { paramCase } from "@/lib/change-case";
import { cn } from "@/lib/utils";

// ----------------------------------------------------------------------

type TDivClassName = Pick<React.HTMLAttributes<HTMLDivElement>, "className">;

interface CustomRHFCheckboxProps<TFieldValues extends FieldValues>
  extends Omit<
      React.ComponentPropsWithoutRef<typeof Controller>,
      "name" | "control" | "render"
    >,
    Omit<
      React.ComponentPropsWithoutRef<typeof Checkbox>,
      "name" | "defaultValue"
    > {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: TDivClassName;
  checkboxContainerClass?: TDivClassName;
  label?: string;
  labelProps?: React.ComponentPropsWithoutRef<typeof Label>;
  displayError?: boolean;
}

export default function CustomRHFCheckbox<TFieldValues extends FieldValues>({
  name,
  helperText,
  defaultValue,
  rules,
  containerClass,
  checkboxContainerClass,
  label,
  labelProps,
  displayError = true,
  ...other
}: CustomRHFCheckboxProps<TFieldValues>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({
        field: { onChange, value, ...fields },
        fieldState: { error },
      }) => (
        <div className={cn("space-y-2", containerClass?.className)}>
          <div className={cn("flex items-center", checkboxContainerClass)}>
            <Checkbox
              id={other.id ?? paramCase(name)}
              {...fields}
              {...other}
              onCheckedChange={onChange}
              checked={value}
            />
            {!!label && (
              <Label
                {...labelProps}
                className={cn(
                  "h-4",
                  !!error && "text-error",
                  labelProps?.className,
                )}
                htmlFor={other.id ?? paramCase(name)}
              >
                {label}
              </Label>
            )}
          </div>
          {!!helperText && (
            <p className="text-xs leading-tight text-muted-foreground">
              {helperText}
            </p>
          )}
          {!!error && displayError && (
            <p className="text-xs leading-tight text-error">{error?.message}</p>
          )}
        </div>
      )}
    />
  );
}
